import {type ReactNode, useEffect} from 'react';
import {Copy, RotateCw} from 'lucide-react';
import {PhoneHQLoader, PhoneHQButton} from 'ch3-ui-lib';
import {useAppDispatch} from '../../../store/store';
import {showToast} from '../../../store/features/Notifications/notificationsSlice';
import {
    useGetWebhookDeliveryQuery,
    useRetryWebhookDeliveryMutation,
    type WebhookDelivery,
    type WebhookDeliveryAttempt,
} from '../../../store/features/integrations/integrationsService';
import {
    dateTime,
    deliveryKind,
    deliveryLabel,
    errorStatus,
    eventTypeLabel,
    isDeliveryInFlight,
    isDeliveryRetryable,
    isSuccessCode,
    nextRetryLabel,
    relativeTime,
    responseText,
    SECTION_LABEL,
    stableHeaders,
} from './webhookModel';
import {CloseButton, DeliveryBadge, Drawer} from './webhookUi';

const POLL_MS = 3_000;

const duration = (ms: number) => ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;

interface DeliveryDrawerProps {
    readonly webhookId: number;
    readonly deliveryId: string;
    readonly webhookEnabled: boolean;
    readonly onClose: () => void;
}

export function DeliveryDrawer({webhookId, deliveryId, webhookEnabled, onClose}: DeliveryDrawerProps) {
    const dispatch = useAppDispatch();
    const {data, isError, refetch} = useGetWebhookDeliveryQuery({id: webhookId, deliveryId});
    const [retryDelivery, {isLoading: retrying}] = useRetryWebhookDeliveryMutation();
    const delivery = data?.delivery;
    const inFlight = delivery ? isDeliveryInFlight(delivery) : false;
    const payload = data?.payload == null ? '' : JSON.stringify(data.payload, null, 2);
    const notify = (description: string, type: 'success' | 'error' = 'success') => dispatch(showToast({description, type}));

    useEffect(() => {
        if (!inFlight) return;
        const timer = window.setInterval(() => { void refetch(); }, POLL_MS);
        return () => window.clearInterval(timer);
    }, [inFlight, refetch]);

    const retry = async () => {
        try {
            await retryDelivery({id: webhookId, deliveryId}).unwrap();
            notify('Retry queued');
        } catch (error) {
            notify(errorStatus(error) === 409 ? 'This delivery is still in progress or the webhook is off.' : 'Delivery could not be retried.', 'error');
        }
    };
    const copyPayload = () => navigator.clipboard.writeText(payload)
        .then(() => notify('Payload copied'))
        .catch(() => notify('Payload could not be copied', 'error'));

    return <Drawer label="Delivery details" onClose={onClose}>
        <div className="flex items-start gap-3 border-b border-line px-5 py-4">
            <div className="min-w-0 flex-1">
                {delivery && <DeliveryBadge delivery={delivery}/>}
                <h2 className="mt-2 text-[17px] font-semibold text-ink">{delivery ? eventTypeLabel(delivery.eventType) : 'Delivery'}</h2>
                <div className="mt-0.5 truncate font-mono text-[11.5px] text-ink-muted">{deliveryId}</div>
            </div>
            <CloseButton onClose={onClose} autoFocus/>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
            {isError && <p role="alert" className="px-5 py-10 text-center text-sm text-danger-ink">Delivery details could not be loaded.</p>}
            {!data && !isError && <div className="flex justify-center py-10"><PhoneHQLoader/></div>}
            {data && delivery && <div className="flex flex-col gap-[22px] px-5 pb-8 pt-[18px]">
                <div className="flex flex-wrap items-center gap-2">
                    {isDeliveryRetryable(delivery) && <PhoneHQButton phq-id="webhookDeliveries-retryDeliveryBtn" variant="secondary" size="sm" disabled={!webhookEnabled || retrying} onClick={() => void retry()}>
                        <RotateCw size={14} className="!p-0"/>{retrying ? 'Retrying…' : delivery.status === 'FAILED' ? 'Retry now' : 'Resend'}
                    </PhoneHQButton>}
                    {payload && <PhoneHQButton variant="secondary" size="sm" onClick={() => void copyPayload()}><Copy size={14} className="!p-0"/>Copy payload</PhoneHQButton>}
                    {!webhookEnabled && isDeliveryRetryable(delivery) && <span className="text-xs text-ink-muted">Turn the webhook on to retry deliveries.</span>}
                </div>

                <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-line">
                    <Fact label="Created" className="border-b border-r">{dateTime(delivery.createdAt)}</Fact>
                    <Fact label="Finished" className="border-b">{dateTime(delivery.finishedAt)}</Fact>
                    <Fact label="Attempts" className="border-r">{delivery.attempts}</Fact>
                    <Fact label="Next retry">{nextRetryLabel(delivery)}</Fact>
                </div>

                <Section title="Attempts">
                    {data.attempts.length === 0 && delivery.lastError === 'WEBHOOK_DISABLED' &&
                        <p className="rounded-lg bg-zinc-50 p-3.5 text-center text-[13px] text-ink-muted">No attempts — the webhook was off when this event happened.</p>}
                    <ol className="flex flex-col">
                        {data.attempts.map(attempt => <AttemptRow key={attempt.attempt} attempt={attempt}/>)}
                        {inFlight && <UpcomingAttempt delivery={delivery}/>}
                    </ol>
                </Section>

                <Section title="Request headers">
                    <div className="overflow-hidden rounded-lg border border-line">
                        {stableHeaders(deliveryId).map(([name, value], index) => <div key={name}
                            className={`grid grid-cols-[150px_1fr] gap-3 px-3 py-2 font-mono text-[11.5px] ${index > 0 ? 'border-t border-line' : ''}`}>
                            <span className="text-ink-muted">{name}</span><span className="break-all text-ink">{value}</span>
                        </div>)}
                    </div>
                    <p className="mt-2 text-xs text-ink-muted">
                        <code className="font-mono">webhook-timestamp</code>, <code className="font-mono">webhook-signature</code> and <code className="font-mono">X-Signature</code> are generated for each attempt.
                    </p>
                </Section>

                <Section title="Payload">
                    {payload
                        ? <pre className="max-h-[360px] overflow-auto rounded-lg bg-[#0b0b0c] px-4 py-3.5 font-mono text-xs leading-relaxed text-zinc-200">{payload}</pre>
                        : <p className="text-[13px] text-ink-muted">The payload is built once the call recording or transcript it includes is ready.</p>}
                </Section>

                <Section title="Response body">
                    <pre className="whitespace-pre-wrap break-all rounded-lg border border-line bg-zinc-50 px-3.5 py-3 font-mono text-xs leading-relaxed text-ink-secondary">
                        {responseBody(delivery, data.attempts.at(-1))}
                    </pre>
                </Section>
            </div>}
        </div>
    </Drawer>;
}

const responseBody = (delivery: WebhookDelivery, last: WebhookDeliveryAttempt | undefined): string =>
    last ? last.responseBody || 'Empty response body.'
        : delivery.lastError === 'WEBHOOK_DISABLED' ? 'Not sent — the webhook was off when this event happened.' : 'Waiting for the first attempt.';

function Fact({label, className = '', children}: {readonly label: string; readonly className?: string; readonly children: ReactNode}) {
    return <div className={`border-line px-3.5 py-3 ${className}`}>
        <div className="text-xs text-ink-muted">{label}</div>
        <div className="mt-0.5 text-[13.5px] text-ink">{children}</div>
    </div>;
}

function Section({title, children}: {readonly title: string; readonly children: ReactNode}) {
    return <section><h3 className={`mb-2.5 ${SECTION_LABEL}`}>{title}</h3>{children}</section>;
}

function TimelineRow({dot, title, detail, time, side}: {readonly dot: string; readonly title: ReactNode; readonly detail: ReactNode; readonly time: string; readonly side: string}) {
    return <li className="grid grid-cols-[14px_1fr_auto] items-start gap-2.5 py-2">
        <span className={`mt-1.5 h-2 w-2 rounded-full ${dot}`}/>
        <div>
            <div className="text-[13.5px] text-ink"><span className="font-medium">{title}</span> · {detail}</div>
            <div className="mt-0.5 text-xs text-ink-disabled">{time}</div>
        </div>
        <div className="mt-0.5 font-mono text-xs text-ink-muted">{side}</div>
    </li>;
}

function AttemptRow({attempt}: {readonly attempt: WebhookDeliveryAttempt}) {
    const ok = isSuccessCode(attempt.statusCode) && !attempt.error;
    return <TimelineRow dot={ok ? 'bg-success' : 'bg-danger'}
        title={<>Attempt {attempt.attempt}{attempt.trigger === 'MANUAL' && <span className="ml-1.5 text-xs font-normal text-ink-muted">manual</span>}</>}
        detail={<span className={ok ? 'text-success-ink' : 'text-danger-ink'}>{responseText(attempt.statusCode, attempt.error)}</span>}
        time={dateTime(attempt.startedAt)} side={duration(attempt.durationMs)}/>;
}

function UpcomingAttempt({delivery}: {readonly delivery: WebhookDelivery}) {
    const title = `Attempt ${delivery.attempts + 1}`;
    switch (deliveryKind(delivery)) {
        case 'retrying':
            return <TimelineRow dot="bg-warning" title={title} detail={<span className="text-warning-ink">Scheduled</span>} time="Exponential backoff" side={nextRetryLabel(delivery)}/>;
        case 'waiting':
            return <TimelineRow dot="bg-fill-subtle" title={title} detail={<span className="text-ink-secondary">{deliveryLabel(delivery)}</span>}
                time={`Next check ${relativeTime(delivery.nextAttemptAt)}`} side=""/>;
        case 'sending':
            return <TimelineRow dot="bg-indigo-500" title={title} detail={<span className="text-indigo-700">Sending</span>} time="In progress" side=""/>;
        default:
            return <TimelineRow dot="bg-indigo-500" title={title} detail={<span className="text-indigo-700">Queued</span>} time="Sending shortly" side="now"/>;
    }
}
